import { useContext } from 'react';
import { IconPlus, IconFolderPlus, IconAlertCircle } from '@tabler/icons-react';
import { Button } from '@/components/ui/button';
import Switch from '@/components/switch/Switch';
import { LangContext } from '@/context/LangProvider';
import { QuestionGroupLimits } from '@/enums';

interface DiscussionGuideHeaderProps {
  showGroupUI: boolean;
  hasQuestions: boolean;
  isRootRandomized: boolean;
  isAddingQuestionsDisabled: boolean;
  isDisabled: boolean;
  isCreatingGroupDisabled: boolean;
  currentQuestionCount: number;
  onAddQuestion: () => void;
  onCreateGroup: () => void;
  onGlobalRandomizeToggle: (enabled: boolean) => void;
}

export function DiscussionGuideHeader({
  showGroupUI,
  hasQuestions,
  isRootRandomized,
  isAddingQuestionsDisabled,
  isDisabled,
  isCreatingGroupDisabled,
  currentQuestionCount,
  onAddQuestion,
  onCreateGroup,
  onGlobalRandomizeToggle,
}: DiscussionGuideHeaderProps) {
  const { lang } = useContext(LangContext);

  // Check if we've reached the question limit
  const hasReachedQuestionLimit = currentQuestionCount >= QuestionGroupLimits.MAX_QUESTIONS;

  return (
    <>
      {/* Header with title */}
      <p className="flex items-center gap-1 text-[15px] font-medium">
        {lang.get('msg.interviewQuestions')}
      </p>

      {/* Action buttons and randomization toggle below title */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex gap-2">
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onAddQuestion}
            disabled={isAddingQuestionsDisabled}
            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white"
          >
            <IconPlus size={14} />
            {lang.get('msg.addQuestion')}
          </Button>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={onCreateGroup}
            disabled={isCreatingGroupDisabled}
            className="flex items-center gap-1.5 bg-pink-600 hover:bg-pink-700 text-white"
          >
            <IconFolderPlus size={14} />
            {lang.get('msg.createGroup')}
          </Button>
        </div>

        {/* Global randomization toggle - show when there are questions or groups */}
        {hasQuestions && (
          <div className="flex items-center gap-2 ml-2">
            <Switch
              value={isRootRandomized}
              func={() => onGlobalRandomizeToggle(!isRootRandomized)}
              isDisabled={isDisabled}
            />
            <label className="text-sm text-gray-700">
              {showGroupUI ? lang.get('msg.randomizeGroups') : lang.get('msg.randomizeQuestions')}
            </label>
          </div>
        )}
      </div>

      {/* Warning message when question limit is reached */}
      {hasReachedQuestionLimit && !isDisabled && (
        <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
          <IconAlertCircle size={18} className="text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-800">
            {lang.get('msg.maxQuestionsLimitReached', {
              limit: String(QuestionGroupLimits.MAX_QUESTIONS),
            })}
          </p>
        </div>
      )}
    </>
  );
}
